import {
  ArrowLeft,
  ArrowRight,
  Globe,
  Link2,
  Loader2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Switch } from "@/modern/components/ui/switch";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { cn } from "@/modern/utils";
import { useContextForm } from "../../context/ContextFormProvider";

const PAGE_PICKER_SKELETON_ROWS = 3;

const pagePath = (url: string) => {
  try {
    const path = new URL(url).pathname;
    return path === "/" ? "/" : path.replace(/\/$/, "");
  } catch {
    return url;
  }
};

const PageCardSkeleton = () => (
  <div className="space-y-2.5">
    {Array.from({ length: PAGE_PICKER_SKELETON_ROWS }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 rounded-xl border border-border/60 bg-muted/20 px-3.5 py-3.5"
      >
        <Skeleton className="h-7 w-7 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-4 w-2/5 max-w-[180px]" />
          <Skeleton className="h-3 w-3/5 max-w-xs" />
        </div>
        <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
      </div>
    ))}
  </div>
);

interface PageCardProps {
  title: string;
  path: string;
  isSelected: boolean;
  isEntry: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const PageCard = ({ title, path, isSelected, isEntry, disabled, onToggle }: PageCardProps) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={disabled}
    aria-pressed={isSelected}
    className={cn(
      "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      disabled && "opacity-50 cursor-not-allowed",
      !disabled && "cursor-pointer",
      isSelected
        ? "border-primary/50 bg-primary/10"
        : "border-border/60 bg-muted/20 hover:border-border hover:bg-muted/40"
    )}
  >
    <div
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
        isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
      )}
    >
      <Link2 className="h-3.5 w-3.5" />
    </div>

    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-foreground truncate leading-tight">
        {title}
      </span>
      <span className="block text-xs text-muted-foreground truncate mt-0.5 leading-snug">
        {path}
      </span>
      {isEntry && (
        <span className="block text-[11px] text-primary/80 mt-0.5">Starting URL</span>
      )}
    </span>

    <Switch
      checked={isSelected}
      tabIndex={-1}
      aria-hidden
      className="pointer-events-none shrink-0"
    />
  </button>
);

export const PagePickerStep = () => {
  const {
    crawledPages,
    selectedUrls,
    setSelectedUrls,
    handleAnalysePages,
    isSubmittingPagePick,
    isLoadingMapUrl,
    boardContextRecord,
    handleBackFromPagePicker,
    canContinueToQuestions,
    handleContinueToQuestions,
    hasOpenAIKey,
  } = useContextForm();

  const hostname = boardContextRecord?.websiteUrl
    ? (() => {
        try {
          return new URL(boardContextRecord.websiteUrl).hostname;
        } catch {
          return boardContextRecord.websiteUrl;
        }
      })()
    : "";

  const toggleUrl = (url: string) => {
    if (!hasOpenAIKey) return;
    if (selectedUrls.includes(url)) {
      setSelectedUrls(selectedUrls.filter((u) => u !== url));
    } else {
      setSelectedUrls([...selectedUrls, url]);
    }
  };

  const selectAll = () => setSelectedUrls(crawledPages.map((p) => p.url));
  const clearAll = () => setSelectedUrls([]);

  const allSelected =
    crawledPages.length > 0 && selectedUrls.length === crawledPages.length;
  const primaryAction = canContinueToQuestions ? handleContinueToQuestions : handleAnalysePages;
  const primaryLabel = canContinueToQuestions
    ? "Continue to questions"
    : `Analyse ${selectedUrls.length} page${selectedUrls.length === 1 ? "" : "s"}`;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Static header */}
      <div className="shrink-0 px-8 py-3.5 border-b border-border/40">
        <h2 className="text-base font-semibold text-foreground">Choose pages to analyse</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {isLoadingMapUrl ? (
            <>
              Mapping pages from{" "}
              <span className="font-medium text-foreground">{hostname || "your site"}</span>…
            </>
          ) : crawledPages.length === 0 ? (
            <>We couldn&apos;t find any pages on this site yet.</>
          ) : (
            <>
              Turn on the pages from{" "}
              <span className="font-medium text-foreground">{hostname}</span> you want included
              in your context.
            </>
          )}
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full px-8 py-4 space-y-4">
          {!isLoadingMapUrl && crawledPages.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <label className="text-sm font-medium text-foreground">Pages to include</label>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {selectedUrls.length} of {crawledPages.length}
                  </span>
                  {crawledPages.length > 1 && (
                    <button
                      type="button"
                      onClick={allSelected ? clearAll : selectAll}
                      disabled={!hasOpenAIKey}
                      className="text-xs font-medium text-primary hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      {allSelected ? "Turn all off" : "Turn all on"}
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2.5">
                {crawledPages.map((page) => {
                  const isSelected = selectedUrls.includes(page.url);
                  const isEntry = page.url === boardContextRecord?.websiteUrl;
                  const path = pagePath(page.url);

                  return (
                    <PageCard
                      key={page.url}
                      title={page.title?.trim() || path || "Untitled page"}
                      path={path}
                      isSelected={isSelected}
                      isEntry={isEntry}
                      disabled={!hasOpenAIKey}
                      onToggle={() => toggleUrl(page.url)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {isLoadingMapUrl && <PageCardSkeleton />}

          {!isLoadingMapUrl && crawledPages.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/10 px-6 py-12 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-muted-foreground/50 mb-3">
                <Globe className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-foreground">No pages discovered</p>
              <p className="mt-1 text-xs text-muted-foreground max-w-xs leading-relaxed">
                Go back and try a different URL, or confirm the site is publicly reachable.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 flex items-center justify-between gap-4 px-8 py-3 border-t border-border/40">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBackFromPagePicker}
          disabled={isLoadingMapUrl || !hasOpenAIKey}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {selectedUrls.length === 0 && !isLoadingMapUrl && crawledPages.length > 0 && (
            <p className="text-xs text-muted-foreground">Turn on at least one page to continue.</p>
          )}
          <Button
            type="button"
            onClick={primaryAction}
            disabled={
              !hasOpenAIKey || isLoadingMapUrl || selectedUrls.length === 0 || isSubmittingPagePick
            }
            className="gap-2"
          >
            {isSubmittingPagePick ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating questions…
              </>
            ) : canContinueToQuestions ? (
              <>
                {primaryLabel}
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {primaryLabel}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
